/**
 * Друкована форма табеля обліку робочого часу ПО ДНЯХ (2026-08-13,
 * рішення власника). Сітка: працівники × дні місяця; відмітки з денних
 * табелів (GET /timesheets/{id}/daily-grid): Р=робота (з годинами),
 * В=вихідний, ВД=відпустка, Л=лікарняний, ПР=прогул. Днів без денного
 * табеля — порожньо. Підсумки — зі рядків місячного табеля.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { timesheets } from '@/api/timesheets';
import { refs } from '@/api/refs';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';

const CODE_LABELS: Record<string, string> = {
  W: 'Р', V: 'ВД', S: 'Л', A: 'ПР', OFF: 'В',
};

const MONTHS = ['січень', 'лютий', 'березень', 'квітень', 'травень', 'червень',
  'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

interface DailyGrid {
  period_year: number;
  period_month: number;
  grid: Record<string, Record<string, { code: string; hours: number }>>;
}

export default function TimesheetPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['timesheet', id],
    queryFn: () => timesheets.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const gridQ = useQuery({
    queryKey: ['timesheet-daily-grid', id],
    queryFn: () => api.get<DailyGrid>(`/timesheets/${id}/daily-grid`).then((r) => r.data),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgsQ.data?.items.find((o) => o.id === d?.organization_id);
  const empQ = useQuery({
    queryKey: ['employees', d?.organization_id],
    queryFn: () => refs.employees(d!.organization_id, {}),
    enabled: !!d?.organization_id,
  });

  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;

  const daysInMonth = new Date(d.period_year, d.period_month, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const empName = new Map((empQ.data ?? []).map((e) => [e.id, e.name]));
  const grid = gridQ.data?.grid ?? {};

  const cell = (empId: string, day: number) => {
    const c = grid[empId]?.[String(day)];
    if (!c) return '';
    // ч.1207: робочий день без годин друкується як «0» (не прочерк і не «Р»).
    if (c.code === 'W') return c.hours != null ? String(c.hours) : 'Р';
    return CODE_LABELS[c.code] ?? c.code;
  };

  return (
    <div className="print-page" style={{
      maxWidth: 1100, margin: '0 auto', padding: '1rem',
      fontFamily: "'Times New Roman', serif", fontSize: '9.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .ts-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 7.5pt; line-height: 1.1; }
        .ts-table th, .ts-table td { border: 1px solid #000; padding: 1px 2px; text-align: center; }
        .ts-table th { font-weight: 600; font-size: 7pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
        .ts-table td.name { text-align: left; padding-left: 4px; white-space: nowrap; }
        @media print { @page { size: A4 landscape; margin: 1cm; } }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.timesheets',
        documentId: d.id,
        filename: `Табель ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.25rem' }}>
        <div style={{ fontSize: '9.5pt' }}>{org.full_name || org.name}</div>
      </div>
      <h3 style={{ textAlign: 'center', margin: '0.4rem 0 0.1rem' }}>
        ТАБЕЛЬ обліку використання робочого часу № {d.document_number}
      </h3>
      <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '9pt', color: '#555' }}>
        за {MONTHS[d.period_month - 1]} {d.period_year} р.
        {' · '}норма: {d.norm_days} роб. днів
      </div>

      <table className="ts-table">
        <thead>
          <tr>
            <th rowSpan={2} style={{ width: 22 }}>№</th>
            <th rowSpan={2} style={{ minWidth: 130 }}>П.І.Б.</th>
            <th colSpan={daysInMonth}>Числа місяця</th>
            <th colSpan={5}>Підсумки, днів</th>
          </tr>
          <tr>
            {days.map((day) => <th key={day} style={{ minWidth: 17 }}>{day}</th>)}
            <th style={{ width: 34 }}>відпр.</th>
            <th style={{ width: 34 }}>відп.</th>
            <th style={{ width: 34 }}>лік.</th>
            <th style={{ width: 34 }}>інші</th>
            <th style={{ width: 40 }}>годин</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td className="name">{empName.get(it.employee_id) ?? '—'}</td>
              {days.map((day) => <td key={day}>{cell(it.employee_id, day)}</td>)}
              <td>{Number(it.working_days)}</td>
              <td>{Number(it.vacation_days) || ''}</td>
              <td>{Number(it.sick_days) || ''}</td>
              <td>{Number(it.absence_days) || ''}</td>
              <td>{Number(it.worked_hours ?? 0) || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ margin: '0.4rem 0', fontSize: '8.5pt', color: '#333' }}>
        Умовні позначення: <b>Р</b>/число — відпрацьовано (годин),{' '}
        <b>В</b> — вихідний, <b>ВД</b> — відпустка, <b>Л</b> — лікарняний,{' '}
        <b>ПР</b> — прогул. Порожня клітинка — денний табель за цей день не
        вівся (підсумки — за місячним табелем).
      </p>

      <table style={{ width: '100%', marginTop: 20, fontSize: '9.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Відповідальна особа</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '8pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Керівник / Головний бухгалтер</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '8pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
